import React, { useEffect, useState, useMemo } from "react";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { db } from "./Firebase";
import FirestoreTable from "./FirestoreTableApp";
import styled, { keyframes } from "styled-components";
import * as XLSX from "xlsx";
import { useData } from "./DataContext";

const ContenedorDiv = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 8px;
  margin: auto;
  padding: 5px;
`;
const ContenedorDivTabla = styled.div`
  display: flex;
  margin: 5px 1rem 5px 1rem;
  max-height: 28rem;
  overflow-x: auto;
  overflow-y: auto;
  justify-content: space-evenly;
  flex-direction: column;
  @media only screen and (min-width: 320px) and (max-width: 768px) {
    margin: 5px 1rem 5px 1rem;
    max-height: 28rem;
  }
`;
const LoadingDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: auto;
  p {
    margin: 0px;
  }
`;
const Loading = keyframes`
    0% {
      width: 0px;
    }
    100% {
      width: 100%;
    }
  `;
const LoadingLine = styled.div`
  width: 100%;
  height: 2px;
  background-color: #c20000;
  border-radius: 2px;
  animation: ${Loading} 3s infinite linear;
`;
const FormGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  flex-direction: row;
  align-items: baseline;
  margin: auto;
  & label {
    font-size: small;
    font-weight: bold;
  }
  & input {
    background-color: rgba(255, 255, 255, 0.4);
    border: 1px solid #ccc;
    border-radius: 2px;
    width: 120px;
    &:focus {
      outline: none;
      color: #1f2124;
    }
  }
`;
const Button = styled.button`
  padding: 5px 10px;
  border: none;
  background-color: #c20000; /* Rojo */
  color: white;
  cursor: pointer;
  &:hover {
    background-color: #6c757d; /* Gris en hover */
  }
`;

function RegistroGimnasia() {
  /*///////////////dataContext/////////////////////////*/
  const { costoUnidadesData } = useData();
  const { costoBusDoble: costoBusDobleData, costoBus40: costoBus40Data } =
    costoUnidadesData[0] || {};

  /*////////////////////////////////////////*/

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const querySnapshot = await getDocs(collection(db, "contratos"));
      let data = querySnapshot.docs.map((doc) => {
        // Cambiar la zona horaria a UTC y formatear las fechas
        let fechaSalida = new Date(
          new Date(doc.data().fechaSalida).toLocaleString("en-US", {
            timeZone: "UTC",
          })
        ).toLocaleDateString("es-ES");
        let fechaRegreso = new Date(
          new Date(doc.data().fechaRegreso).toLocaleString("en-US", {
            timeZone: "UTC",
          })
        ).toLocaleDateString("es-ES");
        return {
          ...doc.data(),
          docId: doc.id,
          fechaOriginal: new Date(doc.data().fechaSalida),
          fechaSalida: fechaSalida,
          fechaRegreso: fechaRegreso,
        };
      });

      // Filtrar los datos por rango de fechas y nombreApellido
      let datosFiltrados = data;
      if (fechaDesde && fechaHasta) {
        const fechaFinAjustada = new Date(fechaHasta);
        fechaFinAjustada.setDate(fechaFinAjustada.getDate() + 1); // Agregamos 1 día

        datosFiltrados = data.filter(
          (item) =>
            item.fechaOriginal >= new Date(fechaDesde) &&
            item.fechaOriginal < fechaFinAjustada && // Cambiamos <= por <
            item.nombreApellido === "CLUB ATLETICO GIMNASIA Y ESGRIMA"
        );
      } else {
        datosFiltrados = data.filter(
          (item) => item.nombreApellido === "CLUB ATLETICO GIMNASIA Y ESGRIMA"
        );
      }

      // Ordenar los datos por fechaOriginal de más reciente a más antiguo
      datosFiltrados.sort((a, b) => b.fechaOriginal - a.fechaOriginal);
      setData(datosFiltrados);
      setLoading(false);
    };
    fetchData();
  }, [fechaDesde, fechaHasta]);

  const deleteRow = async (docId) => {
    if (docId) {
      const docRef = doc(db, "contratos", docId);
      await deleteDoc(docRef);
      // Actualizar los datos
      setData(data.filter((item) => item.docId !== docId));
    } else {
      console.error("Error: Intentando eliminar un documento con una ID vacía");
    }
  };
  //agrega los costos a la tabla
  const datosConCostos = data.map((fila) => {
    const costoPorKm = () => {
      switch (fila.tipoDeServicio) {
        case "urbano":
          return costoBus40Data;
        case "larga distancia":
          return costoBusDobleData;
        case "internacional":
          return costoBusDobleData * 1.6; // 60% más
        default:
          return 0;
      }
    };

    return {
      ...fila,
      costoPorKm: costoPorKm(),
      costoTotal: fila.kmCotizados * costoPorKm(),
    };
  });

  const totalCostoTotal = useMemo(() => {
    return datosConCostos.reduce((acc, fila) => acc + fila.costoTotal, 0);
  }, [datosConCostos]);

  const columns = React.useMemo(
    () => [
      {
        Header: "contrato",
        accessor: "contratoNumero",
        Cell: ({ value }) => value.slice(-12),
      },
      //{ Header: "nombre", accessor: "nombreApellido" },
      { Header: "Fecha Salida", accessor: "fechaSalida" },
      { Header: "Desde", accessor: "origen" },
      { Header: "hora", accessor: "horaSalida" },
      { Header: "Hasta", accessor: "destino" },
      { Header: "Servicio", accessor: "tipoDeServicio" },
      {
        Header: "Tarifa",
        accessor: "tarifaTotal",
        Cell: ({ value }) =>
          value !== undefined ? `$ ${value.toLocaleString("es-AR")}` : "",
      },
      { Header: "Kms", accessor: "kmCotizados" },
      {
        Header: "Costo x km",
        accessor: "costoPorKm",
        Cell: ({ value }) =>
          value !== undefined ? `$ ${value.toLocaleString("es-AR")}` : "",
      },
      {
        Header: "Costo Total",
        accessor: "costoTotal",
        Cell: ({ value }) =>
          value !== undefined ? `$ ${value.toLocaleString("es-AR")}` : "",
      },
      //{ Header: "Observaciones", accessor: "observaciones" },
    ],
    []
  );

  const datosConTotal = useMemo(() => {
    return [
      ...datosConCostos,
      {
        contratoNumero: "Total",
        costoTotal: totalCostoTotal,
      },
    ];
  }, [datosConCostos, totalCostoTotal]);

  const generarPlanilla = () => {
    const datosParaExcel = datosConTotal.map((fila) => ({
      contrato: fila.contratoNumero.slice(-12),
      fecha: fila.fechaSalida,
      origen: fila.origen,
      hora: fila.horaSalida,
      destino: fila.destino,
      servicio: fila.tipoDeServicio,
      tarifa: fila.tarifaTotal,
      kms: fila.kmCotizados,
      costoPorKm: fila.costoPorKm,
      costoTotal: fila.costoTotal,
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(datosParaExcel);
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Servicios Gimnasia y Esgrima"
    );

    //genera el nombre con la fecha seleccionada
    let fecha = new Date(
      new Date(fechaDesde).toLocaleString("en-US", {
        timeZone: "UTC",
      })
    ).toLocaleDateString("es-ES");

    const nombrePlanilla = `Resumen Gimnasia ${fecha}.xlsx`;

    XLSX.writeFile(workbook, nombrePlanilla);
  };

  return (
    <ContenedorDiv>
      <h4>Servicios Gimnasia y Esgrima</h4>
      <FormGroup>
        <FormGroup>
          <label>Fecha Desde:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <label>Fecha Hasta:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </FormGroup>
        <Button onClick={generarPlanilla}>Generar Planilla</Button>
      </FormGroup>
      <ContenedorDivTabla>
        {loading ? (
          <LoadingDiv>
            <p>Cargando datos...</p>
            <LoadingLine></LoadingLine>
          </LoadingDiv>
        ) : (
          <FirestoreTable
            columns={columns}
            data={datosConTotal}
            deleteRow={deleteRow}
          />
        )}
      </ContenedorDivTabla>
    </ContenedorDiv>
  );
}

export default RegistroGimnasia;
